import type { ComponentProps, ReactNode } from "react";

import { cx } from "./cx";

/**
 * The folded mark on its rounded, theme-accent backplate, with each plane referenced
 * from one shared vector source.
 *
 * `/fold-mark.svg` contains the three planes; CSS mixes each plane from the theme accent
 * and accent ink, matching the native apps.
 *
 * Unsized by default and deliberately: an `<svg>` with no size is 300x150, so every caller
 * has to say how big. The header's copy is static markup and takes its 30px from
 * `app.css`; the hero's is below.
 */
export function Mark({ className, ...rest }: ComponentProps<"svg">) {
  return (
    <svg
      className={cx("mark block flex-none", className)}
      viewBox="0 0 1254 1254"
      aria-hidden="true"
      {...rest}
    >
      <rect x="0" y="0" width="1254" height="1254" rx="288" fill="var(--accent)" />
      <use href="/fold-mark.svg#plane-main" />
      <use href="/fold-mark.svg#plane-light" />
      <use href="/fold-mark.svg#plane-overlap" />
    </svg>
  );
}

/**
 * The sign-in front door's masthead.
 *
 * Arrival, and nothing else: the mark, then what you are signing into. Spacing is
 * deliberately larger than the rest of the app -- every other screen is a task with a
 * header above it; this one is a door, and the air around the mark is what makes it read
 * as one.
 *
 * `h1`, not `h2`: the app header is hidden on this screen, so without this the page would
 * start its outline at `h2` with nothing above it. Exactly one `h1` renders either way --
 * signed in, this is not on the page and the header's is the only one left. Sized as a
 * screen title rather than as the header wordmark, because that is what it is doing here;
 * the outline level and the type role are separate questions. Margins zeroed: the gap and
 * padding here set the spacing, and letting the base `h1` rule speak too would double it.
 */
export function Hero({ children }: { children: ReactNode }) {
  return (
    <div className="flex flex-col items-center gap-pad pt-hero-t pb-hero-b text-center">
      <Mark className="size-tile" />
      {/* headline-lg: mono, bold, caps, tracked tight -- the stamped plate over the door. */}
      <h1 className="m-0 font-title text-hero font-bold uppercase tracking-hero">{children}</h1>
    </div>
  );
}
